import { useMutation } from '@tanstack/react-query';
import base from '@/_api/base';
import type {
  ApiResponseError,
  mutationType,
  UseMutationOptions,
} from '@/_api/types';
import { showApiErrorSnack } from '@/lib/show-api-error-snack';

function makeApiCall<T, F, G extends object>(
  requestType: mutationType,
  getEndpoint: (() => string) | ((x: F) => string),
  requestOptions: object | undefined,
  getBody?: (x: F) => G,
): (requestBody: F) => Promise<T> {
  return async (requestBody: F) => {
    const endpoint =
      getEndpoint.length === 0
        ? (getEndpoint as () => string)()
        : (getEndpoint as (x: F) => string)(requestBody);

    let body: unknown = requestBody;
    if (getBody && typeof getBody === 'function') {
      body = getBody(requestBody);
    }

    const opts = requestOptions ?? {};

    switch (requestType) {
      case 'get': {
        if (getEndpoint.length === 0) {
          return base.get<T>(endpoint, opts);
        }
        return base.get<T>(endpoint, {
          ...opts,
          params: body as object,
        });
      }
      case 'post':
        return base.post<T>(endpoint, opts, body);
      case 'put':
        return base.put<T>(endpoint, opts, body);
      case 'delete':
        return base.delete<T>(endpoint, opts, body);
      default: {
        const _exhaustive: never = requestType;
        return _exhaustive;
      }
    }
  };
}

/**
 * TanStack Query v5 mutation wrapper — mirrors legacy `react-query` v3 calling convention.
 * Prefer `getEndpoint.length === 0` for no-variable mutations (e.g. logout).
 */
export function useApiMutation<
  T = unknown,
  F = unknown,
  G extends object = Record<string, never>,
>(
  getEndpoint: (() => string) | ((x: F) => string),
  requestType: mutationType,
  requestOptions: object | undefined,
  mutationOptions: Omit<
    UseMutationOptions<T, ApiResponseError, F>,
    'mutationFn'
  >,
  getBody?: (x: F) => G,
) {
  const { onError, ...restMutationOptions } = mutationOptions;

  return useMutation<T, ApiResponseError, F>({
    ...restMutationOptions,
    mutationFn: makeApiCall<T, F, G>(
      requestType,
      getEndpoint,
      requestOptions,
      getBody,
    ),
    onError: (error, variables, onMutateResult, context) => {
      onError?.(error, variables, onMutateResult, context);
      if (!onError) {
        showApiErrorSnack(error);
      }
    },
  });
}

export default useApiMutation;
